import Button from '@components/Button';
import CountUp from '@components/CountUp';
import Prose from '@components/Prose';
import SimpleDonut from '@components/SimpleDonut';
import { TwoColumnType } from '@data/types';
import useStore from '@store/store';
import classNames from 'classnames';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { hash } from 'utilities/helper';

interface Props extends TwoColumnType {
  as?: keyof JSX.IntrinsicElements;
}

const TwoColumn: React.FC<Props> = ({
  as: Component = 'article',
  htmlContent,
  image,
  backgroundColour = 'blue',
  video,
  button,
  graphs,
}) => {
  const { setPage } = useStore();

  return (
    <Component
      className={classNames('w-full', {
        'bg-blue': backgroundColour === 'blue',
        'bg-white': backgroundColour === 'white',
      })}
    >
      <div className="p-4 xl:px-0 py-16 xl:py-24 w-full max-w-outerGrid h-full flex flex-col lg:flex-row items-center mx-auto gap-12">
        <div
          className={classNames(
            'lg:w-1/2',
            {
              'md:order-1': backgroundColour === 'blue',
            },
            {
              'md:order-2': backgroundColour === 'white',
            }
          )}
        >
          <Prose
            htmlContent={htmlContent}
            style="style-1"
            className={classNames(
              'w-full lg:w-4/5',
              {
                'prose-p:!text-white prose-h3:!text-white':
                  backgroundColour === 'blue',
              },
              {
                'prose-p:!text-blue prose-h3:!text-blue':
                  backgroundColour === 'white',
              }
            )}
          />
          {button && (
            <Button
              {...button}
              type="button"
              onClick={() => {
                window.scrollTo(0, 0);
                setPage('the_assessment');
              }}
            />
          )}
        </div>
        <div
          className={classNames(
            'w-full lg:w-1/2',
            {
              'order-2': backgroundColour === 'blue',
            },
            {
              'md:order-1': backgroundColour === 'white',
            }
          )}
        >
          {image && (
            <figure>
              <img src={image} alt="" />
            </figure>
          )}
          {video && (
            <iframe
              width="560"
              height="315"
              src={video}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              className="rounded-br-[2rem] rounded-tl-[2rem] max-w-full aspect-video"
            ></iframe>
          )}

          {/* Desktop */}
          {graphs && (
            <div className="hidden sm:flex flex-row gap-4">
              {graphs.map((graph) => {
                return (
                  <div
                    className="w-full md:w-1/2 p-8 flex flex-col border bg-white rounded-tl-[2rem] rounded-br-[2rem] border-white border-opacity-20 bg-opacity-[0.06]"
                    key={hash(graph)}
                  >
                    <Percentage value={graph.percentage} text={graph.text} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Mobile */}
          {graphs && (
            <div className="overflow-hidden !w-full md:hidden">
              <Swiper
                className={classNames('flex flex-row w-full', {
                  'swiper--blue': backgroundColour == 'blue',
                })}
                slidesPerView={1}
                spaceBetween={0}
                pagination={graphs.length > 1}
                modules={[Pagination]}
              >
                {graphs.map((graph) => (
                  <SwiperSlide key={hash(graph)} className="grow !h-auto">
                    <div
                      className="w-full h-full p-8 flex flex-col border bg-white rounded-tl-[2rem] rounded-br-[2rem] border-white border-opacity-20 bg-opacity-[0.06]"
                      key={hash(graph)}
                    >
                      <Percentage value={graph.percentage} text={graph.text} />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}
        </div>
      </div>
    </Component>
  );
};

export default TwoColumn;

interface PercentageType {
  value: number;
  text: string;
}

const Percentage: React.FC<PercentageType> = ({ value = 0, text = '' }) => {
  const { ref, inView } = useInView({
    threshold: 1,
  });

  return (
    <>
      <div className="px-8 pb-4" ref={ref}>
        <SimpleDonut percent={inView ? value : 0} />
      </div>
      <div className="text-center text-white leading-normal flex flex-col h-full">
        <p>
          <em className="text-[4rem] text-blue-light not-italic">
            <CountUp value={inView ? value : 0} />%
          </em>
        </p>
        <p>{text}</p>
      </div>
    </>
  );
};
